import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import React from 'react';

import { FileIcon, ImageIcon } from './AttachmentIcons';
import { UploadedFile } from '../../../@types/uploaded-files.type';

export interface AttachmentRowProps {
  file: UploadedFile;
  /** Position in the list, used only for the "File 3" fallback name. */
  index: number;
  onOpen: () => void;
  /** Omit to render the row without an archive action. */
  onArchive?: () => void;
  archiving?: boolean;
}

const nameOf = (file: UploadedFile, index: number): string =>
  file.fileName ?? file.key?.split('/').pop() ?? `File ${index + 1}`;

/** One attachment: its type icon, its name as the open affordance, and — when
 *  the panel offers one — an archive action. */
export const AttachmentRow: React.FC<AttachmentRowProps> = ({
  file,
  index,
  onOpen,
  onArchive,
  archiving = false,
}) => {
  const fileName = nameOf(file, index);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.75,
        px: 1,
        borderRadius: 1,
        '&:hover': { bgcolor: 'v4.tableHover' },
      }}
    >
      {file.mimeType?.startsWith('image/') ? <ImageIcon /> : <FileIcon />}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Link
          component="button"
          underline="hover"
          onClick={onOpen}
          sx={{
            fontSize: '13px',
            fontWeight: 500,
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'left',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {fileName}
        </Link>
      </Box>
      <Typography sx={{ fontSize: '11px', color: 'text.secondary', flexShrink: 0 }}>
        {file.mimeType ?? ''}
      </Typography>
      {onArchive && (
        <IconButton
          size="small"
          aria-label={`Archive ${fileName}`}
          disabled={archiving}
          onClick={onArchive}
          sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'error.main' } }}
        >
          {archiving ? <CircularProgress size={14} /> : <DeleteOutlineIcon sx={{ fontSize: 16 }} />}
        </IconButton>
      )}
    </Box>
  );
};
